// App Review page (PX-377 + PX-380). Top: read-only App Store Connect
// review-state timeline (written by the ASC webhook, PX-376). Bottom: the
// Telegram chat registry — chats the bot was added to (discovered live via the
// inbound Telegram webhook), each with a toggle for whether it receives App
// Review alerts.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '@/lib/api'
import { Toggle } from '@/components/FormControls'
import { useAuth } from '@/lib/auth'
import { hasAnyPerm } from '@/lib/perms'
import {
  PERM,
  type AdminMessageTemplateResponse,
  type AdminTelegramChat,
  type AdminTelegramChatsResponse,
  type AdminTelegramResendResponse,
  type AppReviewAppStatus,
  type AppReviewEvent,
  type AppReviewStatusResponse,
} from '@/types/api'

// State → display. Mirrors the Telegram relay's labels (app_review_service.py
// `_STATE_DISPLAY`) but carries Tailwind tone classes the chat post can't.
// `tone` keys into BADGE_TONE below. Unknown states fall back to neutral + the
// raw state string, same fail-soft as the backend.
type Tone = 'green' | 'blue' | 'amber' | 'red' | 'neutral'
// PX-538: ASC's current state model reports READY_FOR_DISTRIBUTION as the terminal live state.
const LIVE_STATES = new Set(['READY_FOR_SALE', 'READY_FOR_DISTRIBUTION']);

const STATE_DISPLAY: Record<string, { emoji: string; label: string; tone: Tone }> = {
  PREPARE_FOR_SUBMISSION: { emoji: '📝', label: 'Prepare for Submission', tone: 'neutral' },
  READY_FOR_REVIEW: { emoji: '📤', label: 'Ready for Review', tone: 'amber' },
  WAITING_FOR_REVIEW: { emoji: '⏳', label: 'Waiting for Review', tone: 'amber' },
  WAITING_FOR_EXPORT_COMPLIANCE: { emoji: '📋', label: 'Waiting for Export Compliance', tone: 'amber' },
  IN_REVIEW: { emoji: '🔍', label: 'In Review', tone: 'blue' },
  ACCEPTED: { emoji: '✅', label: 'Accepted', tone: 'green' },
  PENDING_DEVELOPER_RELEASE: { emoji: '✅', label: 'Approved — Pending Release', tone: 'green' },
  PENDING_APPLE_RELEASE: { emoji: '✅', label: 'Approved — Pending Apple Release', tone: 'green' },
  PROCESSING_FOR_APP_STORE: { emoji: '⚙️', label: 'Processing for App Store', tone: 'blue' },
  READY_FOR_SALE: { emoji: '🚀', label: 'Live on the App Store', tone: 'green' },
  READY_FOR_DISTRIBUTION: { emoji: '🚀', label: 'Approved — Ready for Distribution', tone: 'green' },
  REJECTED: { emoji: '❌', label: 'Rejected', tone: 'red' },
  METADATA_REJECTED: { emoji: '❌', label: 'Rejected (Metadata)', tone: 'red' },
  DEVELOPER_REJECTED: { emoji: '🛑', label: 'Removed by Developer', tone: 'red' },
  INVALID_BINARY: { emoji: '⚠️', label: 'Invalid Binary', tone: 'red' },
  DEVELOPER_REMOVED_FROM_SALE: { emoji: '🗑', label: 'Removed from Sale', tone: 'neutral' },
}

const BADGE_TONE: Record<Tone, string> = {
  green: 'bg-emerald-100 text-emerald-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
  neutral: 'bg-ink-100 text-ink-600',
}

function display(state: string | null): { emoji: string; label: string; tone: Tone } {
  if (!state) return { emoji: '🔔', label: 'Connected', tone: 'neutral' }
  return STATE_DISPLAY[state] ?? { emoji: '📱', label: state, tone: 'neutral' }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function StateBadge({ state }: { state: string | null }) {
  const d = display(state)
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE_TONE[d.tone]}`}>
      <span>{d.emoji}</span>
      {d.label}
    </span>
  )
}

// Which app a timeline row belongs to (PX-534). Falls back to the raw key so an
// app added to the backend registry still reads sensibly before this map knows
// it — same fail-soft as STATE_DISPLAY.
function AppBadge({ appKey, brand }: { appKey: string; brand?: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">
      {brand ?? appKey}
    </span>
  )
}

function TimelineRow({ event, brand }: { event: AppReviewEvent; brand?: string }) {
  const isPing = event.event_type === 'webhookPingCreated'
  return (
    <tr className="border-b border-ink-50 last:border-b-0 align-top">
      <td className="px-4 py-3 text-xs text-ink-700 whitespace-nowrap">
        {formatDateTime(event.created_at)}
      </td>
      <td className="px-4 py-3">
        <AppBadge appKey={event.app_key} brand={brand} />
      </td>
      <td className="px-4 py-3">
        {isPing ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">
            🔔 Webhook connected
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {event.previous_state && event.previous_state !== event.state && (
              <>
                <StateBadge state={event.previous_state} />
                <span className="text-ink-400">→</span>
              </>
            )}
            <StateBadge state={event.state} />
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink-700">
        {event.version_string ? `v${event.version_string}` : '—'}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-400">
        {event.event_id ?? '—'}
      </td>
    </tr>
  )
}

// One app's current-status card (PX-534). The App Store link uses that app's own
// id — with two apps a hardcoded link would send you to the wrong store page.
function AppHeadlineCard({ app }: { app: AppReviewAppStatus }) {
  const headline = display(app.current_state)
  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-ink-400">{app.brand}</div>
        <code className="rounded bg-ink-100 px-1 py-0.5 text-[11px] text-ink-500">
          {app.app_key}
        </code>
      </div>
      {app.current_state ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-3xl leading-none">{headline.emoji}</span>
            <span className="text-2xl font-semibold tracking-tight">{headline.label}</span>
            {app.current_version && (
              <span className="rounded-md bg-ink-50 px-2 py-1 font-mono text-sm text-ink-700">
                v{app.current_version}
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {LIVE_STATES.has(app.current_state ?? '') && (
              <a
                href={`https://apps.apple.com/app/id${app.app_store_id}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600"
              >
                📲 Open on the App Store
              </a>
            )}
            <span className="ml-auto text-xs text-ink-400">
              as of {formatDateTime(app.current_state_at)}
            </span>
          </div>
        </>
      ) : (
        <div className="mt-2 text-ink-400">No review events yet.</div>
      )}
    </div>
  )
}

export function AppReviewPage() {
  const reviewQuery = useQuery({
    queryKey: ['admin', 'app-review'],
    queryFn: () => request<AppReviewStatusResponse>('/admin/app-review/events'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const data = reviewQuery.data
  const items = data?.items ?? []
  const apps = data?.apps ?? []
  // app_key → brand, for the timeline badges.
  const brandOf = (key: string) => apps.find((a) => a.app_key === key)?.brand

  // Collapse the timeline to the latest few by default (PX-429) — a long review
  // history otherwise pushes the Telegram sections far below the fold.
  const COLLAPSED_COUNT = 5
  const [timelineExpanded, setTimelineExpanded] = useState(false)
  const visibleItems = timelineExpanded ? items : items.slice(0, COLLAPSED_COUNT)
  const hasMore = items.length > COLLAPSED_COUNT

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">App Review</h1>
        <p className="text-sm text-ink-400">
          App Store Connect review status, pushed live via webhook{' '}
          (<code className="rounded bg-ink-100 px-1 py-0.5 text-xs">/api/webhooks/appstore/&lt;app&gt;</code>)
          and relayed to Telegram. Read-only — the timeline is driven entirely by Apple.
          Each app registers its own ASC webhook, since Apple's payload doesn't say
          which app an event belongs to.
        </p>
      </header>

      {/* Current status — one headline per tracked app (PX-534). The app list
          itself comes from the response, so until it lands there are no cards to
          render — hence the explicit loading placeholder. */}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        {apps.map((app) => (
          <AppHeadlineCard key={app.app_key} app={app} />
        ))}
        {apps.length === 0 && (
          <div className="rounded-xl border border-ink-100 bg-white p-5 text-ink-400 shadow-sm">
            {reviewQuery.isLoading ? 'Loading…' : 'No apps configured.'}
          </div>
        )}
      </div>

      {/* Event timeline */}
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-400">Timeline</h2>
      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">App</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3">Event ID</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((event) => (
              <TimelineRow key={event.id} event={event} brand={brandOf(event.app_key)} />
            ))}
            {!reviewQuery.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-ink-400">
                  No events recorded yet. They'll appear here as App Store Connect
                  reports review-status changes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {hasMore && (
          <button
            type="button"
            onClick={() => setTimelineExpanded((v) => !v)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
          >
            {timelineExpanded ? 'Collapse' : `Show all (${items.length})`}
          </button>
        )}
        <p className="text-xs text-ink-400">
          Events come from the <code>app_review_events</code> table, written by the
          signed ASC webhook handler.{' '}
          {hasMore && !timelineExpanded
            ? `Showing latest ${COLLAPSED_COUNT} of ${items.length}.`
            : `${items.length} shown.`}
        </p>
      </div>

      <TelegramChatsSection apps={apps} />
      <MessageTemplateSection apps={apps} />
    </div>
  )
}

// Client-side mirror of app_review_service._render_status_template, for the
// live preview only (PX-378). Example values match a typical transition. The
// template is shared by every app; `{brand}` resolves per app at send time
// (PX-534), so the preview shows one app's brand as an illustration.
function renderPreview(template: string, brand: string): string {
  const label = 'In Review'
  const prev = 'Waiting for Review'
  const subs: Record<string, string> = {
    '{brand}': `${brand} · App Review`,
    '{emoji}': '🔍',
    '{state}': label,
    '{previous_state}': prev,
    '{version}': ' v1.0.0',
    '{transition}': `${prev} → <b>${label}</b>`,
  }
  let out = template
  for (const [token, value] of Object.entries(subs)) out = out.split(token).join(value)
  return out
}

// Editable status-message template (PX-378). Empty = built-in default.
function MessageTemplateSection({ apps }: { apps: AppReviewAppStatus[] }) {
  const { profile } = useAuth()
  // The preview needs a concrete brand to illustrate `{brand}`; any tracked app
  // does. Falls back to a neutral word before the registry loads.
  const previewBrand = apps[0]?.brand ?? 'App'
  const canWrite = hasAnyPerm(profile, [PERM.app_review_write])
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)

  const tmplQuery = useQuery({
    queryKey: ['admin', 'app-review', 'message-template'],
    queryFn: () =>
      request<AdminMessageTemplateResponse>('/admin/app-review/message-template'),
    staleTime: 30_000,
  })

  // Seed the textarea once the server value arrives (and on refetch when the
  // user hasn't started editing).
  useEffect(() => {
    if (tmplQuery.data && draft === null) setDraft(tmplQuery.data.template)
  }, [tmplQuery.data, draft])

  const save = useMutation({
    mutationFn: (template: string) =>
      request<AdminMessageTemplateResponse>('/admin/app-review/message-template', {
        method: 'PATCH',
        body: { template },
      }),
    onSuccess: (res) => {
      setSavedNote(res.template ? 'Saved.' : 'Saved — reverted to built-in default.')
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-review', 'message-template'] })
    },
    onError: () => setSavedNote('Failed to save.'),
  })

  const data = tmplQuery.data
  const value = draft ?? ''
  const effective = value.trim() ? value : data?.default_template ?? ''

  return (
    <section className="mt-8">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-ink-400">
        Status message template
      </h2>
      <p className="mb-3 text-xs text-ink-400">
        Text of the Telegram status post — shared by every tracked app, with{' '}
        <code className="rounded bg-ink-100 px-1 py-0.5 text-[11px]">{'{brand}'}</code>{' '}
        resolving per app at send time (preview shows {previewBrand}). Leave empty
        to use the built-in default. Placeholders:{' '}
        {(data?.placeholders ?? []).map((p) => (
          <code key={p} className="mr-1 rounded bg-ink-100 px-1 py-0.5 text-[11px]">{p}</code>
        ))}
        HTML tags like <code className="rounded bg-ink-100 px-1 py-0.5 text-[11px]">&lt;b&gt;</code> are sent to Telegram as formatting.
      </p>
      <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
        <textarea
          value={value}
          disabled={!canWrite}
          onChange={(e) => { setDraft(e.target.value); setSavedNote(null) }}
          rows={4}
          placeholder={data?.default_template ?? ''}
          className="w-full rounded-md border border-ink-200 bg-ink-50 px-3 py-2 font-mono text-xs text-ink-900 focus:border-brand-400 focus:outline-none disabled:opacity-60"
        />
        <div className="mt-3">
          <div className="text-xs uppercase tracking-wide text-ink-400">Preview</div>
          <pre className="mt-1 whitespace-pre-wrap rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-800">
            {renderPreview(effective, previewBrand)}
          </pre>
        </div>
        {canWrite && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate(value)}
              className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              disabled={save.isPending || !value}
              onClick={() => { setDraft(''); setSavedNote(null) }}
              className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear (use default)
            </button>
            {savedNote && <span className="text-xs text-ink-500">{savedNote}</span>}
          </div>
        )}
      </div>
    </section>
  )
}

function chatLabel(chat: AdminTelegramChat): string {
  if (chat.title) return chat.title
  return `${chat.type ?? 'chat'} ${chat.chat_id}`
}

// Telegram chats the bot belongs to + per-chat App Review alert toggle (PX-380).
// The list is discovered live — a chat appears once the bot is added to it.
function TelegramChatsSection({ apps }: { apps: AppReviewAppStatus[] }) {
  const { profile } = useAuth()
  const canWrite = hasAnyPerm(profile, [PERM.app_review_write])
  const queryClient = useQueryClient()
  // PX-381: chats ticked for a manual resend.
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [resendNote, setResendNote] = useState<string | null>(null)
  // PX-534: WHOSE status a manual resend sends. Defaults to the first app once
  // the registry loads — a resend has to name an app, there's no "all" scope.
  const [resendAppKey, setResendAppKey] = useState<string | null>(null)
  const effectiveAppKey = resendAppKey ?? apps[0]?.app_key ?? null

  const chatsQuery = useQuery({
    queryKey: ['admin', 'app-review', 'telegram-chats'],
    queryFn: () => request<AdminTelegramChatsResponse>('/admin/app-review/telegram-chats'),
    staleTime: 30_000,
  })

  const toggle = useMutation({
    mutationFn: ({ chatId, enabled }: { chatId: number; enabled: boolean }) =>
      request<AdminTelegramChat>(`/admin/app-review/telegram-chats/${chatId}`, {
        method: 'PATCH',
        body: { app_review_alerts_enabled: enabled },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'app-review', 'telegram-chats'] }),
  })

  // PX-381: manual resend of the current status / history digest to the ticked
  // chats — e.g. to seed a freshly-added group.
  const resend = useMutation({
    mutationFn: (scope: 'current' | 'history') =>
      request<AdminTelegramResendResponse>('/admin/app-review/telegram-resend', {
        method: 'POST',
        body: { chat_ids: [...selected], scope, app_key: effectiveAppKey },
      }),
    onSuccess: (res, scope) =>
      setResendNote(`Sent ${scope} to ${res.sent}/${res.requested} chat(s).`),
    onError: () => setResendNote('Failed to send — see status / try again.'),
  })

  // PX-382: bot leaves a group (Telegram leaveChat) / manual row cleanup.
  const invalidateChats = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'app-review', 'telegram-chats'] })
  const leave = useMutation({
    mutationFn: (chatId: number) =>
      request<AdminTelegramChat>(`/admin/app-review/telegram-chats/${chatId}/leave`, {
        method: 'POST',
      }),
    onSuccess: invalidateChats,
  })
  const remove = useMutation({
    mutationFn: (chatId: number) =>
      request(`/admin/app-review/telegram-chats/${chatId}`, { method: 'DELETE' }),
    onSuccess: (_res, chatId) => {
      setSelected((prev) => {
        const next = new Set(prev)  // drop the removed row from any selection
        next.delete(chatId)
        return next
      })
      invalidateChats()
    },
  })
  const actionPending = leave.isPending || remove.isPending

  const chats = chatsQuery.data?.items ?? []

  function toggleSelected(chatId: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) next.delete(chatId)
      else next.add(chatId)
      return next
    })
    setResendNote(null)
  }

  const canResend =
    canWrite && selected.size > 0 && !resend.isPending && effectiveAppKey !== null

  return (
    <section className="mt-8">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-ink-400">
        Telegram chats
      </h2>
      <p className="mb-3 text-xs text-ink-400">
        Chats the bot has been added to (discovered automatically). Toggle which
        ones receive App Review status alerts — a chat enabled here gets alerts for
        every tracked app. Tick chats, pick an app, and use “Send …” to push that
        app's current status or full history (handy for a freshly-added group).
      </p>
      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3 w-8"></th>
              <th className="px-4 py-3">Chat</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Alerts</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {chats.map((chat) => (
              <tr key={chat.chat_id} className="border-b border-ink-50 last:border-b-0">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={selected.has(chat.chat_id)}
                    disabled={!canWrite}
                    onChange={() => toggleSelected(chat.chat_id)}
                    aria-label={`Select ${chatLabel(chat)}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="text-ink-900">{chatLabel(chat)}</div>
                  <div className="font-mono text-[11px] text-ink-400">{chat.chat_id}</div>
                </td>
                <td className="px-4 py-3 text-xs text-ink-700">{chat.type ?? '—'}</td>
                <td className="px-4 py-3">
                  {chat.is_active ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                      active
                    </span>
                  ) : (
                    <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">
                      bot removed
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <Toggle
                      checked={chat.app_review_alerts_enabled}
                      disabled={!canWrite || !chat.is_active || toggle.isPending}
                      onChange={(v) => toggle.mutate({ chatId: chat.chat_id, enabled: v })}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {/* Leave — only for an active group/supergroup/channel the bot
                        can actually leave (you can't leave a private chat). */}
                    {chat.is_active && chat.type !== 'private' && (
                      <button
                        type="button"
                        disabled={!canWrite || actionPending}
                        onClick={() => {
                          if (confirm(`Leave “${chatLabel(chat)}”? The bot will leave this chat.`))
                            leave.mutate(chat.chat_id)
                        }}
                        className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:border-amber-300 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Leave
                      </button>
                    )}
                    {/* Remove from list — manual cleanup of a `bot removed` row. */}
                    {!chat.is_active && (
                      <button
                        type="button"
                        disabled={!canWrite || actionPending}
                        onClick={() => {
                          if (confirm(`Remove “${chatLabel(chat)}” from the list?`))
                            remove.mutate(chat.chat_id)
                        }}
                        className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Remove from list
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!chatsQuery.isLoading && chats.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-ink-400">
                  No chats yet. Add the Telegram bot to a chat or group to see it here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-ink-400">
            {selected.size} selected
          </span>
          {/* Which app's status gets sent (PX-534) — a resend always names one app. */}
          <label className="flex items-center gap-2 text-xs text-ink-400">
            App
            <select
              value={effectiveAppKey ?? ''}
              onChange={(e) => { setResendAppKey(e.target.value); setResendNote(null) }}
              className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm text-ink-900 focus:border-brand-400 focus:outline-none"
            >
              {apps.map((app) => (
                <option key={app.app_key} value={app.app_key}>{app.brand}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={!canResend}
            onClick={() => resend.mutate('current')}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send current status
          </button>
          <button
            type="button"
            disabled={!canResend}
            onClick={() => resend.mutate('history')}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send history
          </button>
          {resendNote && <span className="text-xs text-ink-500">{resendNote}</span>}
        </div>
      )}
    </section>
  )
}
